import React, { useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { shallowEqual, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { getCurrentUserRole, selectComplianceEnabled } from 'features/auth/ducks/selectors'
import { selectCurrentBpHasTaskManagement, selectHasAccessToBp } from 'features/bpDetails/ducks/selectors'
import CookieService from 'services/CookieService'
import useBodyScrollLock from 'hooks/useBodyScrollLock'

import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined'
import { ReactComponent as OtherDocumentsIcon } from 'assets/images/folders/documents.svg'
import { ReactComponent as AccessControlIcon } from 'assets/images/folders/access_control.svg'
import { ReactComponent as UnitsIcon } from 'assets/images/folders/units.svg'

import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import InboxIcon from '@mui/icons-material/MoveToInbox'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Box from '@mui/material/Box'

export const BOTTOM_BAR_LABELS = {
  guru: 'Guru',
  documents: 'Documents',
  units: 'Units',
  accessControl: 'Access',
  more: 'More'
}

export const formatBarCount = n => (n > 999 ? '999+' : Number(n).toLocaleString('en-GB'))

export const bottomBarItemLabel = (key, count) => {
  const label = BOTTOM_BAR_LABELS[key] || key
  return count > 0 ? `${label}, ${Number(count).toLocaleString('en-GB')}` : label
}

const sameId = (a, b) => a != null && b != null && String(a) === String(b)

export const bottomBarCounts = (state, bpId) => {
  const files = state.files?.filesCounts
  const units = state.units
  const people = state.bpDetails?.peopleCount
  return {
    documents: files && (files.bpId == null || sameId(files.bpId, bpId)) ? Number(files.total) || 0 : 0,
    units: units && sameId(units.bpUnitsBpId, bpId) ? Number(units.bpUnitsTotal) || 0 : 0,
    accessControl: people && sameId(people.bpId, bpId) ? Number(people.total) || 0 : 0
  }
}

const BOTTOM_BAR_VAR = '--bp-bottom-bar'
const BOTTOM_BAR_HEIGHT = 'calc(68px + env(safe-area-inset-bottom, 0px))'

const BottomBar = ({ isloggedin = true }) => {
  const userRole = useSelector(getCurrentUserRole)
  const complianceEnabled = useSelector(selectComplianceEnabled)
  const currentBpHasTaskManagement = useSelector(selectCurrentBpHasTaskManagement)
  const hasFullAccess = useSelector(selectHasAccessToBp)
  const location = useLocation()
  const params = useParams()
  const [isMoreOpen, setIsMoreOpen] = useState(false)

  useBodyScrollLock(isMoreOpen)

  const pathname = location?.pathname || ''
  const bpBase = PRIVATE_PATHS.bpList

  const match = useMemo(() => {
    try {
      return pathname.match(new RegExp(`^${bpBase}/([^/]+)(?:/([^/]+))?`))
    } catch (e) {
      const escaped = String(bpBase).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return pathname.match(new RegExp(`^${escaped}/([^/]+)(?:/([^/]+))?`))
    }
  }, [pathname, bpBase])

  const routeId = params?.id || match?.[1] || null
  const routeFolder = params?.folder || match?.[2] || null
  const tabCounts = useSelector(state => bottomBarCounts(state, routeId), shallowEqual)

  const isBpDetails = Boolean(match?.[1])

  const isFrsUser = userRole === 'emergency_service'
  const isResponsiblePerson = userRole === 'responsible_person'

  const activeFolder = routeFolder || (routeId ? CookieService.getLastFolder(routeId) : null) || 'units'

  const goToFolder = (folderKey, opts = {}) => {
    if (!routeId) return

    setIsMoreOpen(false)

    CookieService.setLastFolder(routeId, folderKey)

    if (opts.selectAllFiles) {
      CookieService.set(`selectAllFiles_${routeId}`, 'true')
    }

    redirect(`${bpBase}/${routeId}/${folderKey}`)
  }

  const handleMoreClick = () => {
    setIsMoreOpen(true)
  }
  const handleClose = () => {
    setIsMoreOpen(false)
  }

  const moreMenuItems = useMemo(() => [
    {
      key: 'wiki',
      label: 'Overview',
      visible: hasFullAccess,
      icon: <MenuBookOutlinedIcon />
    },
    {
      key: 'reporting',
      label: 'Reports',
      visible: hasFullAccess,
      icon: <AssessmentOutlinedIcon />
    },
    {
      key: 'planning',
      label: 'Site Risks',
      visible: hasFullAccess,
      icon: <PublicOutlinedIcon />
    },
    {
      key: 'compliance',
      label: 'Compliance',
      visible: complianceEnabled && currentBpHasTaskManagement,
      icon: <AssignmentTurnedInOutlinedIcon />
    },
    {
      key: 'taskReports',
      label: 'Task Reports',
      visible: hasFullAccess && currentBpHasTaskManagement,
      icon: <DescriptionOutlinedIcon />
    },
    {
      key: 'inbox',
      label: 'Inbox',
      visible: true,
      icon: <InboxIcon />
    },
    {
      key: 'calendar',
      label: 'Calendar',
      visible: true,
      icon: <EventOutlinedIcon />,
      onClick: () => {
        setIsMoreOpen(false)
        if (routeId) redirect(`/calendar?bp=${routeId}`)
      }
    },
    {
      key: 'frs',
      label: 'FRS Documents',
      visible: isFrsUser,
      icon: <LocalFireDepartmentOutlinedIcon />
    },
    {
      key: 'noticeBoard',
      label: 'Notice Board',
      visible: !hasFullAccess,
      icon: <CampaignOutlinedIcon />
    }
  ].filter(x => x.visible), [hasFullAccess, complianceEnabled, currentBpHasTaskManagement, isFrsUser, routeId])

  const isMoreActive = moreMenuItems.some(item => item.key === activeFolder)

  const mainItems = useMemo(() => {
    return [
      {
        key: 'guru',
        visible: !isFrsUser && !isResponsiblePerson,
        active: activeFolder === 'guru',
        onClick: () => goToFolder('guru'),
        icon: <AssistantOutlinedIcon />
      },
      {
        key: 'documents',
        visible: true,
        active: activeFolder === 'other' || activeFolder === 'files',
        onClick: () => goToFolder('other', { selectAllFiles: true }),
        icon: <OtherDocumentsIcon />
      },
      {
        key: 'units',
        visible: true,
        active: activeFolder === 'units',
        onClick: () => goToFolder('units'),
        icon: <UnitsIcon />
      },
      {
        key: 'accessControl',
        visible: !isFrsUser,
        active: activeFolder === 'accessControl',
        onClick: () => goToFolder('accessControl'),
        icon: <AccessControlIcon />
      },
      {
        key: 'more',
        visible: true,
        active: isMoreActive || isMoreOpen,
        onClick: handleMoreClick,
        icon: <MoreHorizIcon />
      }
    ].filter((x) => x.visible)
  }, [activeFolder, isFrsUser, isResponsiblePerson, routeId, bpBase, isMoreActive, isMoreOpen])

  const shouldRender = Boolean(isloggedin && isBpDetails && routeId)

  // Publish the bar's footprint so floating controls (compliance pill, Guru
  // launcher) can stack above it — and sit low on the screens where the bar
  // is not rendered at all, such as the dashboard.
  useEffect(() => {
    const root = document.documentElement
    if (shouldRender) root.style.setProperty(BOTTOM_BAR_VAR, BOTTOM_BAR_HEIGHT)
    else root.style.removeProperty(BOTTOM_BAR_VAR)
    return () => root.style.removeProperty(BOTTOM_BAR_VAR)
  }, [shouldRender])

  if (!shouldRender) return null

  return (
    <>
      <BottomBarWrapper>
        <BottomBarInner>
          <BottomNav>
            {mainItems.map((item) => (
              <BottomNavItem
                key={item.key}
                type="button"
                onClick={item.onClick}
                aria-label={bottomBarItemLabel(item.key, tabCounts[item.key])}
                title={BOTTOM_BAR_LABELS[item.key] || item.key}
                aria-current={item.active ? 'page' : undefined}
              >
                <IconWrap $active={item.active}>
                  {item.icon}
                  {tabCounts[item.key] > 0 && <CountPill aria-hidden='true'>{formatBarCount(tabCounts[item.key])}</CountPill>}
                </IconWrap>
                <Caption $active={item.active}>{BOTTOM_BAR_LABELS[item.key] || item.key}</Caption>
              </BottomNavItem>
            ))}
          </BottomNav>
        </BottomBarInner>
      </BottomBarWrapper>

      <Drawer
        anchor="bottom"
        open={isMoreOpen}
        onClose={handleClose}
        disableScrollLock={true}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            paddingBottom: 'env(safe-area-inset-bottom)',
            backgroundColor: '#ffffff',
            boxShadow: '0px -4px 20px rgba(0,0,0,0.1)'
          }
        }}
        sx={{ zIndex: 6000 }}
      >
        <Box sx={{ width: '100%' }} role="presentation">
          <PullerWrapper>
            <Puller />
          </PullerWrapper>

          <List sx={{ pt: 0, pb: 2 }}>
            {moreMenuItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  onClick={item.onClick || (() => goToFolder(item.key))}
                  selected={activeFolder === item.key}
                  sx={{
                    py: 2,
                    px: 3,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(210, 71, 36, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(210, 71, 36, 0.12)',
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{
                    color: activeFolder === item.key ? '#d14724' : '#64748b',
                    minWidth: 44
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 16,
                      fontWeight: activeFolder === item.key ? 600 : 500,
                      color: activeFolder === item.key ? '#d14724' : '#1e293b'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  )
}

export default BottomBar

const BottomBarWrapper = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 5000;

    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);

    border-top: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.06);

    padding-bottom: env(safe-area-inset-bottom);
  }
`

const BottomBarInner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 2px 2px;
  height: 64px;
  box-sizing: border-box;
`

const BottomNav = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 4px 3px;
`

const BottomNavItem = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  padding: 4px 6px;
  min-width: 56px;
  min-height: 48px;
  border-radius: 12px;
  cursor: pointer;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;

  &:focus-visible {
    outline: 2px solid #33becc;
    outline-offset: 2px;
  }

`

const IconWrap = styled.div`
  position: relative;
  width: 40px;
  height: 30px;
  border-radius: 12px;

  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $active, theme }) =>
    $active
      ? css`
        background: ${theme.colors.primary}14;
        color: ${theme.colors.primary};
        & * {
          fill: ${theme.colors.primary} !important;
        }
      `
      : css`
        background: transparent;
        color: #334155;
        & * {
          fill: #334155 !important;
        }
      `}

  svg {
    font-size: 24px;
    width: 24px;
    height: 24px;
  }
`

const Caption = styled.span`
  font-size: 12px;
  line-height: 1.2;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : '#334155')};
  white-space: nowrap;
`

const CountPill = styled.span`
  position: absolute;
  top: -4px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 999px;
  background: #e6f7f9;
  color: #0f6f78;
  border: 1px solid #ffffff;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  font-variant-numeric: tabular-nums;

  & * { fill: none !important; }
`

const PullerWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 12px;
  padding-bottom: 8px;
`

const Puller = styled.div`
  width: 36px;
  height: 4px;
  background-color: #e2e8f0;
  border-radius: 2px;
`
